import type { CustomTodoItem } from "@core/types/todo";

/**
 * 주간 숙제 정렬 키.
 * - 고정 항목: "SILMAEL" | "ELYSIAN" | "HAL" | "HELLKEY" | "CUBE"
 * - 커스텀 숙제: "CUSTOM:<customTodoId>"
 */
export const WEEK_TODO_FIXED_KEYS = [
  "SILMAEL",
  "ELYSIAN",
  "HAL",
  "HELLKEY",
  "CUBE",
] as const;

export type WeekTodoFixedKey = (typeof WEEK_TODO_FIXED_KEYS)[number];

export const customTodoKey = (customTodoId: number): string =>
  `CUSTOM:${customTodoId}`;

/**
 * 저장된 weekTodoOrder 문자열을 파싱하여 해당 캐릭터에 유효한 순서를 반환한다.
 *
 * 규칙:
 * 1. 저장 문자열을 콤마로 split, 공백 제거
 * 2. 고정 키는 그대로, CUSTOM:<id>는 해당 characterId의 WEEKLY 커스텀에 존재하는 것만 유지 (stale 정리)
 * 3. 빠진 고정 항목 + 빠진 커스텀 항목을 기본 순서로 끝에 append
 * 4. 저장값이 비어있거나 전부 무효면 `[...FIXED, ...customIds]` 기본값
 */
export const parseWeekTodoOrder = (
  stored: string,
  customTodos: CustomTodoItem[],
  characterId: number
): string[] => {
  // 해당 캐릭터의 WEEKLY 커스텀 숙제만 대상 (다른 캐릭터 / DAILY 섹션 제외)
  const weeklyCustoms = customTodos.filter(
    (t) => t.frequency === "WEEKLY" && t.characterId === characterId
  );
  const validCustomIds = new Set(weeklyCustoms.map((t) => t.customTodoId));
  const validKeys = new Set<string>([
    ...WEEK_TODO_FIXED_KEYS,
    ...Array.from(validCustomIds).map(customTodoKey),
  ]);

  const seen = new Set<string>();
  const result: string[] = [];

  const pushIfNew = (key: string) => {
    if (!seen.has(key)) {
      seen.add(key);
      result.push(key);
    }
  };

  if (stored) {
    stored.split(",").forEach((raw) => {
      const key = raw.trim();
      // 빈 값 / 알 수 없는 키 / 삭제된 CUSTOM:<id> / 중복은 무시
      if (!key || !validKeys.has(key) || seen.has(key)) return;
      seen.add(key);
      result.push(key);
    });
  }

  // 빠진 고정 항목을 기본 순서로 append
  WEEK_TODO_FIXED_KEYS.forEach(pushIfNew);

  // 빠진 커스텀 항목은 customTodoId 오름차순으로 append
  Array.from(validCustomIds)
    .sort((a, b) => a - b)
    .forEach((id) => pushIfNew(customTodoKey(id)));

  return result;
};

/**
 * 정렬 키가 커스텀 숙제인지 판별.
 */
export const isCustomKey = (key: string): boolean => key.startsWith("CUSTOM:");

/**
 * "CUSTOM:<id>" 키에서 customTodoId를 추출.
 */
export const getCustomId = (key: string): number | null => {
  if (!isCustomKey(key)) return null;
  const id = Number(key.slice("CUSTOM:".length));
  return Number.isFinite(id) ? id : null;
};
